import {
  FDBodyText, FDBox, FDPrimaryActionButton, FDStack,
} from '@facet-wealth/design';
import { PieChartWithLegend } from 'Packages/FacetDesign/Components/Experimental/PieChart/PieChart';
import React, { useMemo, useState } from 'react';
import useMobileLayout from 'Hooks/useMobileLayout';
import { currency } from 'Helpers/Formats';
import PieChartLegendItem from 'Packages/FacetDesign/Components/Experimental/PieChart/PieChartLegendItem';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { DialogContent } from 'Views/Page/Content/Modal/Components';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import fr from 'facet-rest';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { dataAttrs } from 'Helpers/Testing';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box/Box';
import { SelectedIndexProvider } from 'Packages/FacetDesign/Components/Experimental/PieChart/selectedIndexContext';

export const Layout = () => {
  const { resource } = useMetaLayoutContext();
  const [activeIndex, setActiveIndex] = useState(undefined);
  const isMobile = useMobileLayout();
  const {
    loadModalCollectionByLink,
  } = useModalContext();
  const { displayErrorMessage } = useSnackbarContext();
  const [loading, setLoading] = useState(false);

  function handleLegendOnClick({ index }) {
    setActiveIndex(index);
  }

  function handleChartOnClick({ activeTooltipIndex }) {
    setActiveIndex(activeTooltipIndex);
  }

  const labelFormatter = (value, entry) => {
    const { payload } = entry;
    const formattedValue = payload?.value ? ` (${currency(payload.value, true)})` : '';
    return (<FDBodyText>{value}{formattedValue}</FDBodyText>);
  };

  const handleOpen = async (rel) => {
    try {
      setLoading(true);
      await loadModalCollectionByLink(rel.link('related').url());
      setLoading(false);
    } catch (err) {
      displayErrorMessage('Unable to open detailed summary');
      setLoading(false);
    }
  };

  const renderLegend = (legendData) => {
    const { payload } = legendData;
    const width = isMobile ? '100%' : 400;
    const sortedPayload = payload.sort((a, b) => b.payload.value - a.payload.value);
    return (
      <Box sx={{ marginTop: '1rem' }}> { /* Spacing to prevent recharts legend overflowing over chart */ }
        <FDStack direction={'column'} paddingY={2} spacing={isMobile ? 1 : 2} width={width} maxHeight={isMobile ? 275 : 375} overflow={'auto'}>
          {sortedPayload.map((entry, index) => (
            <PieChartLegendItem item={entry} selected={index === activeIndex} index={index} onLegendClick={() => handleLegendOnClick({ index })} />
          ))}
        </FDStack>
      </Box>
    );
  };

  const chartData = useMemo(() => resource?.attr('chartData')?.resources().list().map((c) => {
    return {
      type: c.id(),
      name: c.attr('label'),
      value: parseFloat(c.attr('value')),
      color: c.attr('color'),
      description: c.attr('description'),
    };
  }), [resource]);

  return (
    <>
      <MetaControlField attr="title" />
      <DialogContent>
        <FDStack direction={'column'} spacing={isMobile ? undefined : 4} flexGrow={1}>
          <MetaControlField attr="subtitle" />
          <FDBox paddingX={isMobile ? undefined : 2} height={'100%'}>
            <SelectedIndexProvider selectedIndex={activeIndex}>
              <PieChartWithLegend
                renderLegend={renderLegend}
                labelFormatter={labelFormatter}
                handleChartOnClick={handleChartOnClick}
                legendProps={isMobile ? {
                  verticalAlign: 'bottom',
                } : {
                  align: 'right',
                  verticalAlign: 'middle',
                }}
                data={chartData}
                labelText={resource.attr('totalValue')}
                activeIndex={activeIndex}
                isDonut
              />
            </SelectedIndexProvider>
          </FDBox>
          {resource.withRelationship(fr.relationships.detailedSummary.name,
            (rel) => (
              <FDStack justifyContent={'center'} width={'100%'}>
                <FDPrimaryActionButton
                  loading={loading}
                  label={rel.meta('prompt')}
                  onClick={() => handleOpen(rel)}
                  {...dataAttrs('assumptions-and-details-button')}
                />
              </FDStack>),
            () => null)}
        </FDStack>
      </DialogContent>
    </>);
};
